/** Pagina CER attivi — catalogo preferiti . */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusCircle, Pencil, Trash2, Tag, Star, Package, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { CerAttivoDialog } from "@/components/CerAttivoDialog";
import { ApiError, endpoints } from "@/api/client";
import {
  listCerCatalog,
  removeCerCatalog,
  type CerCatalogEntry,
} from "@/lib/cerCatalog";
import { formatEerDisplay } from "@/lib/movimento";
import { choiceLabelByValue, PROVENIENZA, STATO_FISICO } from "@/lib/rentriChoices";

function apiToLocal(
  rows: Awaited<ReturnType<typeof endpoints.cerCatalog.list>>,
): CerCatalogEntry[] {
  return rows.map((r) => ({
    id: r.id,
    codice: r.codice_eer,
    descrizione: r.descrizione ?? "",
    stato_fisico: r.stato_fisico ?? "",
    provenienza: (r.provenienza === "U" ? "U" : "S") as "U" | "S",
    scadenza_analisi: r.scadenza_analisi ?? undefined,
    predefinito: r.is_default,
    created_at: new Date().toISOString(),
  }));
}

function formatScadenza(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("it-IT");
}

export function CerAttivi() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [edit, setEdit] = useState<CerCatalogEntry | null>(null);

  const openNew = () => {
    setEdit(null);
    setDialogOpen(true);
  };

  const openEdit = (row: CerCatalogEntry) => {
    setEdit(row);
    setDialogOpen(true);
  };

  const listQ = useQuery({
    queryKey: ["cer-catalog"],
    queryFn: async () => {
      try {
        const rows = await endpoints.cerCatalog.list();
        if (!Array.isArray(rows)) {
          throw new Error("Risposta cer-catalog non valida");
        }
        return { source: "api" as const, items: apiToLocal(rows) };
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status >= 500)) {
          return { source: "local" as const, items: listCerCatalog() };
        }
        throw e;
      }
    },
    refetchOnMount: "always",
  });

  const billingQ = useQuery({
    queryKey: ["billing-status"],
    queryFn: endpoints.billing.status,
    enabled: listQ.data?.source === "api",
  });

  const quotaQ = useQuery({
    queryKey: ["billing-cer-quota"],
    queryFn: endpoints.billing.cerQuota,
    enabled: listQ.data?.source === "api",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("pack_success") !== "1") return;
    void endpoints.billing
      .sync()
      .then(() => {
        toast.success("Pacchetto +5 CER attivato");
        void qc.invalidateQueries({ queryKey: ["billing-status"] });
        void qc.invalidateQueries({ queryKey: ["billing-cer-quota"] });
        window.history.replaceState({}, "", "/cer-attivi");
      })
      .catch((e: unknown) =>
        toast.error(e instanceof Error ? e.message : "Errore attivazione pack CER"),
      );
  }, [qc]);

  const packMut = useMutation({
    mutationFn: () => {
      const origin = window.location.origin;
      return endpoints.billing.checkout({
        kind: "cer_pack",
        success_url: `${origin}/cer-attivi?pack_success=1`,
        cancel_url: `${origin}/cer-attivi?pack_canceled=1`,
      });
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Errore acquisto pack"),
  });

  const removeMut = useMutation({
    mutationFn: async (id: string) => {
      if (listQ.data?.source === "api") {
        await endpoints.cerCatalog.remove(id);
      } else {
        removeCerCatalog(id);
      }
    },
    onSuccess: () => {
      toast.success("CER rimosso dall'elenco");
      qc.invalidateQueries({ queryKey: ["cer-catalog"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore eliminazione"),
  });

  const handleRemove = (row: CerCatalogEntry) => {
    const label = formatEerDisplay(row.codice);
    if (
      !window.confirm(
        `Rimuovere il CER ${label} dall'elenco preferiti?\nI movimenti già inviati non sono modificati.`,
      )
    ) {
      return;
    }
    removeMut.mutate(row.id);
  };

  const items = listQ.data?.items ?? [];
  const billing = billingQ.data;
  const slotsFull =
    billing != null && billing.cer_used >= billing.cer_slots && billing.subscription_active;
  const overQuota = (quotaQ.data?.cer_over_quota ?? 0) > 0 && billing?.subscription_active;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Tag className="h-7 w-7" /> CER attivi
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Anagrafica dei codici rifiuto usati di frequente nei registri e nei formulari.
            Precompila movimenti e FIR con i codici dell&apos;elenco preferiti.
            {listQ.data?.source === "local" &&
              " (solo locale — avvia il backend per sincronizzare sul server)"}
          </p>
        </div>
        <Button onClick={openNew} disabled={slotsFull}>
          <PlusCircle className="mr-2 h-4 w-4" /> Nuovo CER
        </Button>
      </div>

      {billing && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
            <div className="flex items-start gap-3">
              <Package className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">
                  Slot CER: {billing.cer_used} / {billing.cer_slots} utilizzati
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Il piano include 5 CER; puoi aggiungere pacchetti da 5 CER a €20 + IVA
                  all&apos;anno, che si rinnovano insieme all&apos;abbonamento.
                </p>
                {overQuota && quotaQ.data && (
                  <p className="text-xs text-amber-800 mt-1">
                    Nei movimenti e nei formulari risultano {quotaQ.data.cer_used_effective} CER
                    diversi: servono {quotaQ.data.packs_needed} pacchetti aggiuntivi.
                  </p>
                )}
              </div>
            </div>
            {(slotsFull || overQuota) && (
              <Button onClick={() => packMut.mutate()} disabled={packMut.isPending}>
                <CreditCard className="mr-2 h-4 w-4" />
                Acquista +5 CER (€20 + IVA/anno)
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base">Elenco CER preferiti</CardTitle>
          {items.length > 0 && (
            <span className="text-xs text-muted-foreground">{items.length} codici</span>
          )}
        </CardHeader>
        <CardContent>
          {listQ.isPending ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Caricamento…</p>
          ) : listQ.isError ? (
            <p className="text-sm text-destructive py-10 text-center">
              {listQ.error instanceof Error ? listQ.error.message : "Errore caricamento CER"}
            </p>
          ) : items.length === 0 ? (
            <div className="py-12 text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Nessun CER attivo. Aggiungi i codici che usi più spesso per velocizzare carichi,
                scarichi e formulari.
              </p>
              <Button onClick={openNew} variant="outline">
                <PlusCircle className="mr-2 h-4 w-4" /> Aggiungi il primo CER
              </Button>
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Codice</TH>
                  <TH>Descrizione</TH>
                  <TH>Stato fisico</TH>
                  <TH>Provenienza</TH>
                  <TH>Scadenza analisi</TH>
                  <TH className="text-right">Azioni</TH>
                </TR>
              </THead>
              <TBody>
                {items.map((row) => (
                  <TR
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openEdit(row)}
                  >
                    <TD className="font-mono text-sm whitespace-nowrap">
                      {formatEerDisplay(row.codice)}
                      {row.predefinito && (
                        <Badge variant="secondary" className="ml-2 gap-0.5">
                          <Star className="h-3 w-3 fill-current" />
                          predefinito
                        </Badge>
                      )}
                    </TD>
                    <TD className="max-w-[280px] truncate" title={row.descrizione}>
                      {row.descrizione || "—"}
                    </TD>
                    <TD className="text-sm max-w-[200px]">
                      {choiceLabelByValue(row.stato_fisico, STATO_FISICO)}
                    </TD>
                    <TD className="text-sm">
                      {choiceLabelByValue(row.provenienza, PROVENIENZA)}
                    </TD>
                    <TD className="text-xs whitespace-nowrap">
                      {formatScadenza(row.scadenza_analisi)}
                    </TD>
                    <TD className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8"
                        onClick={() => openEdit(row)}
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" />
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-destructive hover:text-destructive"
                        onClick={() => handleRemove(row)}
                        disabled={removeMut.isPending}
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" />
                        Elimina
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CerAttivoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entry={edit}
        useApi={listQ.data?.source !== "local"}
        onSaved={() => qc.invalidateQueries({ queryKey: ["cer-catalog"] })}
      />
    </div>
  );
}
